import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button, Table, Badge, Alert, ProgressBar, Tabs, Tab, Modal } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaArrowLeft, FaSearch, FaFileExcel, FaDownload, FaUpload, FaCheck, FaTimes, FaCheckCircle, FaExclamationTriangle, FaClipboardList, FaUtensils, FaInfoCircle, FaSave } from "react-icons/fa";
import axios from "axios";
import ModalConfirmacion from "../../../components/modals/ModalConfirmacion";

const API_URL = process.env.REACT_APP_PYTHON_API_URL || "http://localhost:8003";

const MantenimientoRecetas = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // Estados principales
  const [recetas, setRecetas] = useState([]);
  const [insumos, setInsumos] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState({
    recetaSeleccionada: null,
    filtro: "",
  });

  // Tab principal
  const [activeMainTab, setActiveMainTab] = useState('individual'); // 'individual' o 'masiva'

  // Modal CRUD (solo para tab individual)
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' o 'edit'
  const [formData, setFormData] = useState({ 
    codigo_interno: '', 
    nombre: '', 
    descripcion: '',
    calorias_por_porcion: '',
    detalles: []
  });

  // Modal de confirmación de eliminación
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [recetaToDelete, setRecetaToDelete] = useState(null);

  // Modal de indicaciones
  const [showIndicacionesModal, setShowIndicacionesModal] = useState(false);

  // Carga Masiva (para tab masiva)
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [uploadStep, setUploadStep] = useState("select"); // 'select', 'validating', 'validated', 'uploading', 'success'

  // ========== Cargar datos ==========
  const fetchRecetas = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/mantenimientos/planificacion-insumos/recetas/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { with_stats: true }
      });
      setRecetas(response.data);
    } catch (error) {
      console.error("Error al cargar recetas:", error);
      toast.error("Error al cargar recetas");
    } finally {
      setLoading(false);
    }
  };

  const fetchInsumos = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/mantenimientos/planificacion-insumos/insumos/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInsumos(response.data);
    } catch (error) {
      console.error("Error al cargar insumos:", error);
      toast.error("Error al cargar insumos");
    }
  };

  const fetchUnidades = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/mantenimientos/planificacion-insumos/unidades-medida/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnidades(response.data);
    } catch (error) {
      console.error("Error al cargar unidades:", error);
      toast.error("Error al cargar unidades de medida");
    }
  };

  useEffect(() => {
    fetchRecetas();
    fetchInsumos();
    fetchUnidades();
  }, []);

  // Filtrar recetas
  const recetasFiltradas = recetas.filter((receta) => {
    const matchNombre = receta.nombre.toLowerCase().includes(state.filtro.toLowerCase()) ||
                       (receta.codigo_interno && receta.codigo_interno.toLowerCase().includes(state.filtro.toLowerCase()));
    return matchNombre;
  });

  // ========== CRUD Handlers ==========
  const handleOpenCreateModal = () => {
    setModalMode('create');
    setFormData({ 
      codigo_interno: '', 
      nombre: '', 
      descripcion: '',
      calorias_por_porcion: '',
      detalles: []
    });
    setShowModal(true);
  };

  const handleOpenEditModal = async (receta) => {
    setModalMode('edit');
    try {
      // Cargar receta completa con detalles
      const response = await axios.get(
        `${API_URL}/api/mantenimientos/planificacion-insumos/recetas/${receta.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const recetaCompleta = response.data;
      
      setFormData({ 
        id: recetaCompleta.id,
        codigo_interno: recetaCompleta.codigo_interno || '', 
        nombre: recetaCompleta.nombre || '', 
        descripcion: recetaCompleta.descripcion || '',
        calorias_por_porcion: recetaCompleta.calorias_por_porcion || '',
        detalles: recetaCompleta.detalles || []
      });
      setShowModal(true);
    } catch (error) {
      toast.error("Error al cargar los detalles de la receta");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({ 
      codigo_interno: '', 
      nombre: '', 
      descripcion: '',
      calorias_por_porcion: '',
      detalles: []
    });
  };

  const handleAddDetalle = () => {
    setFormData({
      ...formData,
      detalles: [...formData.detalles, { insumo_id: '', cantidad: '', unidad_uso_id: '' }]
    });
  };

  const handleRemoveDetalle = (index) => {
    const nuevosDetalles = formData.detalles.filter((_, i) => i !== index);
    setFormData({ ...formData, detalles: nuevosDetalles });
  };

  const handleUpdateDetalle = (index, field, value) => {
    const nuevosDetalles = [...formData.detalles];
    nuevosDetalles[index][field] = value;
    setFormData({ ...formData, detalles: nuevosDetalles });
  };

  const handleSaveReceta = async (closeModal = false) => {
    // Validaciones básicas
    if (!formData.codigo_interno || formData.codigo_interno.trim().length < 1) {
      toast.error("El código interno es obligatorio");
      return;
    }

    if (!formData.nombre || formData.nombre.trim().length < 3) {
      toast.error("El nombre debe tener al menos 3 caracteres");
      return;
    }

    // Validar detalles
    const detallesInvalidos = formData.detalles.some(d => 
      !d.insumo_id || !d.cantidad || !d.unidad_uso_id || parseFloat(d.cantidad) <= 0
    );

    if (formData.detalles.length > 0 && detallesInvalidos) {
      toast.error("Todos los detalles deben tener insumo, cantidad mayor a 0 y unidad de medida");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        codigo_interno: formData.codigo_interno.trim(),
        nombre: formData.nombre.trim(),
        descripcion: formData.descripcion?.trim() || null,
        calorias_por_porcion: formData.calorias_por_porcion ? parseFloat(formData.calorias_por_porcion) : 0,
        detalles: formData.detalles.map(d => ({
          insumo_id: parseInt(d.insumo_id),
          cantidad: parseFloat(d.cantidad),
          unidad_uso_id: parseInt(d.unidad_uso_id)
        }))
      };

      if (modalMode === 'create') {
        const response = await axios.post(
          `${API_URL}/api/mantenimientos/planificacion-insumos/recetas/`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Receta creada exitosamente");
        // Actualizar el formData con el ID de la receta creada para poder seguir editando
        if (response.data && response.data.id) {
          setFormData({ ...formData, id: response.data.id });
          setModalMode('edit'); // Cambiar a modo edición para futuras actualizaciones
        }
      } else {
        await axios.put(
          `${API_URL}/api/mantenimientos/planificacion-insumos/recetas/${formData.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Receta actualizada exitosamente");
      }
      fetchRecetas();
      
      if (closeModal) {
        handleCloseModal();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al guardar receta";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReceta = (receta) => {
    if (!receta) {
      toast.error("Por favor seleccione una receta para eliminar");
      return;
    }

    // Validación previa: verificar si tiene detalles asociados
    if (receta.total_detalles > 0) {
      toast.error(
        `La receta "${receta.nombre}" tiene ${receta.total_detalles} detalle${receta.total_detalles > 1 ? 's' : ''} asociado${receta.total_detalles > 1 ? 's' : ''}. Se eliminarán también los detalles.`
      );
    }

    setRecetaToDelete(receta);
    setShowConfirmModal(true);
  };

  const confirmDeleteReceta = async () => {
    if (!recetaToDelete) return;

    setLoading(true);
    try {
      await axios.delete(
        `${API_URL}/api/mantenimientos/planificacion-insumos/recetas/${recetaToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Receta eliminada exitosamente");
      setState({ ...state, recetaSeleccionada: null });
      fetchRecetas();
      setShowConfirmModal(false);
      setRecetaToDelete(null);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al eliminar receta";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // ========== Carga Masiva ==========
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/mantenimientos/planificacion-insumos/recetas/plantilla/excel`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "plantilla_recetas.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Plantilla descargada exitosamente");
    } catch (error) {
      toast.error("Error al descargar plantilla");
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
        toast.error("El archivo debe ser formato Excel (.xlsx o .xls)");
        return;
      }
      setSelectedFile(file);
      setUploadStep("select");
      setValidationResult(null);
    }
  };

  const handleValidateFile = async () => {
    if (!selectedFile) {
      toast.error("Por favor seleccione un archivo");
      return;
    }

    setUploadLoading(true);
    setUploadStep("validating");

    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", selectedFile);

      const response = await axios.post(
        `${API_URL}/api/mantenimientos/planificacion-insumos/recetas/carga-masiva/validar`,
        formDataUpload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setValidationResult(response.data);
      setUploadStep("validated");

      if (response.data.filas_validas > 0) {
        toast.success(
          `Validación exitosa: ${response.data.filas_validas} filas válidas de ${response.data.total_filas}`
        );
      } else {
        toast.warning("No hay filas válidas para cargar");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al validar archivo";
      toast.error(errorMsg);
      setUploadStep("select");
    } finally {
      setUploadLoading(false);
    }
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile || !validationResult || validationResult.filas_validas === 0) {
      toast.error("No hay datos válidos para cargar");
      return;
    }

    setUploadLoading(true);
    setUploadStep("uploading");

    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", selectedFile);

      const response = await axios.post(
        `${API_URL}/api/mantenimientos/planificacion-insumos/recetas/carga-masiva/confirmar`,
        formDataUpload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.success(response.data.message);
      setUploadStep("success");
      fetchRecetas();

      setTimeout(() => {
        handleResetUpload();
      }, 2000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error en carga masiva";
      toast.error(errorMsg);
      setUploadStep("validated");
    } finally {
      setUploadLoading(false);
    }
  };

  const handleResetUpload = () => {
    setSelectedFile(null);
    setValidationResult(null);
    setUploadStep("select");
  };

  // Helper para obtener nombre de insumo
  const getInsumoNombre = (insumoId) => {
    const insumo = insumos.find(i => i.id === parseInt(insumoId));
    return insumo ? insumo.nombre : 'N/A';
  };

  // Helper para obtener nombre de unidad
  const getUnidadNombre = (unidadId) => {
    const unidad = unidades.find(u => u.id === parseInt(unidadId));
    return unidad ? unidad.codigo : 'N/A';
  };

  return (
    <>
      {/* Digital Harmony Styles */}
      <style>
        {`
          .dh-maintenance-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }

          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 1.25rem 0;
            margin-bottom: 1.5rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }

          .dh-main-title {
            font-size: 1.75rem;
            font-weight: 700;
            margin: 0;
          }

          .dh-main-subtitle {
            font-size: 0.95rem;
            opacity: 0.9;
            margin: 0.5rem 0 0 0;
          }

          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }

          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1rem 1.5rem;
            border: none;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            font-weight: 700;
            font-size: 0.95rem;
          }

          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 8px 16px;
            font-weight: 600;
            font-size: 0.875rem;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }

          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }

          .btn-dh-warning {
            background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 8px 16px;
            font-weight: 600;
            font-size: 0.875rem;
            box-shadow: 0 4px 14px rgba(251, 191, 36, 0.3);
            transition: all 0.3s ease;
          }

          .btn-dh-warning:hover {
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(251, 191, 36, 0.4);
            color: white;
          }

          .btn-dh-danger {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 8px 16px;
            font-weight: 600;
            font-size: 0.875rem;
            box-shadow: 0 4px 14px rgba(239, 68, 68, 0.3);
            transition: all 0.3s ease;
          }

          .btn-dh-danger:hover {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
            color: white;
          }

          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 8px 16px;
            font-weight: 600;
            font-size: 0.875rem;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }

          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }

          .btn-dh-info {
            background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 8px 16px;
            font-weight: 600;
            font-size: 0.875rem;
            box-shadow: 0 4px 14px rgba(59, 130, 246, 0.3);
            transition: all 0.3s ease;
          }

          .btn-dh-info:hover {
            background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
            color: white;
          }

          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 8px 12px;
            font-size: 0.875rem;
            transition: all 0.3s ease;
            background: rgba(255, 255, 255, 0.9);
          }

          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
            background: white;
          }

          .dh-enhanced-table {
            border-collapse: separate;
            border-spacing: 0;
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.05);
            background: white;
          }

          .dh-enhanced-table thead th {
            background: #374151 !important;
            color: white !important;
            font-weight: 700 !important;
            font-size: 0.7rem !important;
            text-transform: uppercase !important;
            padding: 0.5rem 0.4rem !important;
            border: none !important;
            position: sticky !important;
            top: 0 !important;
            z-index: 10 !important;
          }

          .dh-enhanced-table tbody td {
            padding: 0.4rem !important;
            border: none;
            border-bottom: 1px solid #f3f4f6;
            font-size: 0.75rem;
            vertical-align: middle;
          }

          .dh-enhanced-table tbody tr:hover {
            background: linear-gradient(135deg, #f9fafb 0%, rgba(102, 126, 234, 0.02) 100%) !important;
          }

          .dh-enhanced-table tbody tr.selected {
            background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%) !important;
            border-left: 4px solid #667eea;
          }

          .dh-checkbox {
            width: 20px;
            height: 20px;
            border: 2px solid #d1d5db;
            border-radius: 6px;
            background: white;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .dh-checkbox:checked {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-color: #667eea;
          }

          .dh-badge-info {
            background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
            color: white;
            border-radius: 8px;
            padding: 4px 10px;
            font-weight: 600;
            font-size: 0.75rem;
          }

          .nav-tabs {
            margin-bottom: 0.75rem;
            margin-top: 0;
          }
        `}
      </style>

      <div className="dh-maintenance-bg">
        <ToastContainer />
        <Container fluid className="py-4">
          <Row>
            <Col lg={12}>
              <Card className="dh-card">
                <Card.Header className="dh-card-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Mantenimiento Recetas</h5>
                    <Button
                      className="btn-dh-secondary d-flex align-items-center"
                      onClick={() => navigate("/home")}
                      style={{ background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)', border: 'none', color: 'white', fontSize: '0.8rem', padding: '6px 12px' }}
                    >
                      <FaArrowLeft className="me-2" />
                      Volver
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body style={{ padding: '1rem' }}>
                  <Tabs
                    activeKey={activeMainTab}
                    onSelect={(k) => setActiveMainTab(k)}
                    className="mb-4"
                  >
                    {/* TAB 1: Mantenimiento Individual */}
                    <Tab
                      eventKey="individual"
                      title={
                        <span>
                          <FaClipboardList className="me-2" />
                          Mantenimiento Individual
                        </span>
                      }
                    >
                      {/* Botones de acción */}
                      <div className="d-flex justify-content-end gap-2 mb-4">
                        <Button
                          onClick={handleOpenCreateModal}
                          className="btn-dh-primary d-flex align-items-center"
                        >
                          <FaPlus className="me-2" />
                          Nuevo
                        </Button>
                        <Button
                          className="btn-dh-warning d-flex align-items-center"
                          onClick={() => {
                            if (state.recetaSeleccionada) {
                              handleOpenEditModal(state.recetaSeleccionada);
                            } else {
                              toast.error("Por favor seleccione una receta para editar");
                            }
                          }}
                        >
                          <FaEdit className="me-2" />
                          Editar
                        </Button>
                        <Button
                          className="btn-dh-danger d-flex align-items-center"
                          onClick={() => handleDeleteReceta(state.recetaSeleccionada)}
                          disabled={!state.recetaSeleccionada}
                        >
                          <FaTrash className="me-2" />
                          Eliminar
                        </Button>
                      </div>

                      {/* Búsqueda */}
                      <div className="mb-4">
                        <div className="position-relative">
                          <Form.Control
                            type="search"
                            className="dh-form-control"
                            placeholder="Buscar receta por nombre o código interno..."
                            value={state.filtro}
                            onChange={(e) => setState({ ...state, filtro: e.target.value })}
                            style={{ paddingLeft: '3rem' }}
                          />
                          <FaSearch
                            className="position-absolute"
                            style={{
                              left: '1rem',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: '#6b7280'
                            }}
                          />
                        </div>
                      </div>

                      {/* Tabla */}
                      <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                        <Table className="dh-enhanced-table">
                          <thead>
                            <tr>
                              <th style={{ width: "50px" }}></th>
                              <th>Código Interno</th>
                              <th>Nombre</th>
                              <th>Descripción</th>
                              <th className="text-center">Calorías</th>
                              <th className="text-center" style={{ width: "150px" }}>Detalles</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading ? (
                              <tr>
                                <td colSpan="6" className="text-center py-5">
                                  <div className="d-flex flex-column align-items-center">
                                    <div className="spinner-border text-primary mb-3" role="status">
                                      <span className="visually-hidden">Cargando...</span>
                                    </div>
                                    <span className="text-muted">Cargando recetas...</span>
                                  </div>
                                </td>
                              </tr>
                            ) : recetasFiltradas.length === 0 ? (
                              <tr>
                                <td colSpan="6" className="text-center py-5">
                                  <Alert variant="info" className="mb-0">
                                    <FaUtensils className="me-2" />
                                    No se encontraron recetas
                                  </Alert>
                                </td>
                              </tr>
                            ) : (
                              recetasFiltradas.map((receta) => (
                                <tr
                                  key={receta.id}
                                  className={state.recetaSeleccionada?.id === receta.id ? 'selected' : ''}
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => setState({ ...state, recetaSeleccionada: receta })}
                                >
                                  <td>
                                    <Form.Check
                                      type="checkbox"
                                      className="dh-checkbox"
                                      checked={state.recetaSeleccionada?.id === receta.id}
                                      onChange={() => {}}
                                    />
                                  </td>
                                  <td style={{ fontWeight: '600', color: '#374151' }}>{receta.codigo_interno}</td>
                                  <td>{receta.nombre}</td>
                                  <td>{receta.descripcion || '-'}</td>
                                  <td className="text-center">
                                    {receta.calorias_por_porcion ? `${receta.calorias_por_porcion} kcal` : '-'}
                                  </td>
                                  <td className="text-center">
                                    <Badge className="dh-badge-info">
                                      {receta.total_detalles || 0}
                                    </Badge>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Tab>

                    {/* TAB 2: Carga Masiva */}
                    <Tab
                      eventKey="masiva"
                      title={
                        <span>
                          <FaFileExcel className="me-2" />
                          Carga Masiva
                        </span>
                      }
                    >
                      {uploadStep === "select" && (
                        <>
                          <div className="d-flex justify-content-between align-items-center mb-4">
                            <Alert variant="info" className="mb-0 flex-grow-1 me-3" style={{ fontSize: '0.875rem' }}>
                              <h6 className="fw-bold mb-2" style={{ fontSize: '0.95rem' }}>
                                <FaCheckCircle className="me-2" />
                                Instrucciones para Carga Masiva
                              </h6>
                              <ol className="mb-0" style={{ fontSize: '0.85rem', paddingLeft: '1.25rem' }}>
                                <li className="mb-1">Descargue la plantilla Excel usando el botón</li>
                                <li className="mb-1">Complete los datos en el archivo Excel siguiendo el formato indicado</li>
                                <li className="mb-1">Guarde el archivo en su computadora</li>
                                <li className="mb-1">Seleccione el archivo completado</li>
                                <li>Valide los datos y confirme la carga</li>
                              </ol>
                            </Alert>

                            <div className="d-flex gap-2">
                              <Button
                                className="btn-dh-info"
                                onClick={() => setShowIndicacionesModal(true)}
                                style={{ minWidth: '150px', height: 'fit-content' }}
                              >
                                <FaInfoCircle className="me-2" />
                                Indicaciones
                              </Button>
                              <Button
                                className="btn-dh-primary"
                                onClick={handleDownloadTemplate}
                                style={{ minWidth: '200px', height: 'fit-content' }}
                              >
                                <FaDownload className="me-2" />
                                Descargar Template
                              </Button>
                            </div>
                          </div>

                          <Card style={{ border: '2px solid #3b82f6', borderRadius: '15px' }}>
                            <Card.Body className="text-center py-3">
                              <FaUpload size={35} className="text-primary mb-2" />
                              <h6 className="fw-bold mb-2" style={{ fontSize: '0.95rem' }}>Seleccionar Archivo Excel</h6>
                              <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>Suba el archivo Excel completado para validar</p>
                              <div className="d-flex justify-content-center">
                                <Form.Control
                                  type="file"
                                  className="dh-form-control"
                                  accept=".xlsx,.xls"
                                  onChange={handleFileSelect}
                                  style={{ maxWidth: '500px' }}
                                />
                              </div>
                              {selectedFile && (
                                <Alert variant="success" className="mt-2 mb-0 mx-auto" style={{ maxWidth: '500px', fontSize: '0.85rem' }}>
                                  <FaCheckCircle className="me-2" />
                                  <strong>{selectedFile.name}</strong>
                                </Alert>
                              )}
                            </Card.Body>
                          </Card>

                          {selectedFile && (
                            <div className="text-center mt-3">
                              <Button
                                className="btn-dh-info"
                                onClick={handleValidateFile}
                                disabled={uploadLoading}
                              >
                                <FaCheckCircle className="me-2" />
                                Validar Archivo
                              </Button>
                            </div>
                          )}
                        </>
                      )}

                      {uploadStep === "validating" && (
                        <div className="text-center py-4">
                          <div className="spinner-border text-primary mb-2" role="status" style={{ width: '2rem', height: '2rem' }}>
                            <span className="visually-hidden">Validando...</span>
                          </div>
                          <h6 className="mb-2" style={{ fontSize: '0.95rem' }}>Validando archivo Excel...</h6>
                          <p className="text-muted" style={{ fontSize: '0.85rem' }}>Por favor espere mientras se validan los datos</p>
                        </div>
                      )}

                      {uploadStep === "validated" && validationResult && (
                        <>
                          <Alert variant={validationResult.filas_validas > 0 ? "success" : "warning"} style={{ fontSize: '0.875rem' }}>
                            <h6 className="fw-bold mb-2" style={{ fontSize: '0.95rem' }}>
                              <FaCheckCircle className="me-2" />
                              Resultado de Validación
                            </h6>
                            <Row className="text-center">
                              <Col md={4}>
                                <div className="p-2">
                                  <h4 className="mb-0" style={{ fontSize: '1.5rem' }}>{validationResult.total_filas}</h4>
                                  <small className="text-muted" style={{ fontSize: '0.8rem' }}>Total Filas</small>
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="p-2">
                                  <h4 className="mb-0 text-success" style={{ fontSize: '1.5rem' }}>{validationResult.filas_validas}</h4>
                                  <small className="text-muted" style={{ fontSize: '0.8rem' }}>Filas Válidas</small>
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="p-2">
                                  <h4 className="mb-0 text-danger" style={{ fontSize: '1.5rem' }}>{validationResult.filas_invalidas}</h4>
                                  <small className="text-muted" style={{ fontSize: '0.8rem' }}>Filas Inválidas</small>
                                </div>
                              </Col>
                            </Row>
                          </Alert>

                          {validationResult.errores.length > 0 && (
                            <Alert variant="danger" style={{ fontSize: '0.875rem' }}>
                              <h6 className="fw-bold mb-2" style={{ fontSize: '0.9rem' }}>
                                <FaExclamationTriangle className="me-2" />
                                Errores encontrados ({validationResult.errores.length})
                              </h6>
                              <div style={{ maxHeight: '250px', overflowY: 'auto' }} className="mt-2">
                                <Table size="sm" striped bordered className="mb-0" style={{ fontSize: '0.8rem' }}>
                                  <thead>
                                    <tr>
                                      <th style={{ width: '80px' }}>Fila</th>
                                      <th style={{ width: '120px' }}>Campo</th>
                                      <th>Error</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {validationResult.errores.map((error, idx) => (
                                      <tr key={idx}>
                                        <td className="text-center"><Badge bg="danger">{error.fila}</Badge></td>
                                        <td><code>{error.campo}</code></td>
                                        <td>{error.error}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            </Alert>
                          )}

                          <div className="d-flex justify-content-center gap-3 mt-3">
                            <Button className="btn-dh-secondary" onClick={handleResetUpload}>
                              <FaTimes className="me-2" />
                              Seleccionar otro archivo
                            </Button>
                            {validationResult.filas_validas > 0 && (
                              <Button
                                className="btn-dh-primary"
                                onClick={handleConfirmUpload}
                                disabled={uploadLoading}
                              >
                                <FaUpload className="me-2" />
                                Confirmar Carga ({validationResult.filas_validas} filas)
                              </Button>
                            )}
                          </div>
                        </>
                      )}

                      {uploadStep === "uploading" && (
                        <div className="text-center py-4">
                          <div className="spinner-border text-success mb-2" role="status" style={{ width: '2rem', height: '2rem' }}>
                            <span className="visually-hidden">Cargando...</span>
                          </div>
                          <h6 className="mb-2" style={{ fontSize: '0.95rem' }}>Cargando datos al sistema...</h6>
                          <p className="text-muted" style={{ fontSize: '0.85rem' }}>Insertando {validationResult?.filas_validas} registros</p>
                          <ProgressBar animated now={100} className="mt-2" style={{ height: '18px' }} />
                        </div>
                      )}

                      {uploadStep === "success" && (
                        <Alert variant="success" className="text-center py-3" style={{ fontSize: '0.875rem' }}>
                          <FaCheckCircle size={40} className="text-success mb-2" />
                          <h5 className="fw-bold mb-2" style={{ fontSize: '1rem' }}>¡Carga completada exitosamente!</h5>
                          <p className="mb-2" style={{ fontSize: '0.85rem' }}>Los datos han sido insertados correctamente en el sistema</p>
                          <Button className="btn-dh-primary" onClick={handleResetUpload}>
                            <FaPlus className="me-2" />
                            Cargar otro archivo
                          </Button>
                        </Alert>
                      )}
                    </Tab>
                  </Tabs>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Modal CRUD para Recetas */}
      {showModal && (
        <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
          <Modal.Header style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            padding: '0.75rem 1rem'
          }}>
            <Modal.Title className="fw-bold" style={{ fontSize: '0.9rem' }}>
              <FaUtensils className="me-2" />
              {modalMode === 'create' ? 'Nueva Receta' : 'Editar Receta'}
            </Modal.Title>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={handleCloseModal}
            />
          </Modal.Header>
          <Modal.Body style={{ padding: '0.75rem', maxHeight: '70vh', overflowY: 'auto', fontSize: '0.85rem' }}>
            <Form onSubmit={(e) => { e.preventDefault(); handleSaveReceta(); }}>
              {/* Campos de Cabecera */}
              <Row className="mb-1">
                <Col md={6}>
                  <Form.Group className="mb-0">
                    <Form.Label className="fw-bold" style={{ fontSize: '0.8rem', marginBottom: '0.2rem' }}>Código Interno *</Form.Label>
                    <Form.Control
                      type="text"
                      className="dh-form-control"
                      placeholder="Ej: REC-001"
                      value={formData.codigo_interno || ''}
                      onChange={(e) => setFormData({...formData, codigo_interno: e.target.value})}
                      required
                      maxLength={50}
                      style={{ fontSize: '0.8rem', padding: '0.4rem 0.6rem' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-0">
                    <Form.Label className="fw-bold" style={{ fontSize: '0.8rem', marginBottom: '0.2rem' }}>Calorías por Porción</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      className="dh-form-control"
                      placeholder="Ej: 350.50"
                      value={formData.calorias_por_porcion || ''}
                      onChange={(e) => setFormData({...formData, calorias_por_porcion: e.target.value})}
                      style={{ fontSize: '0.8rem', padding: '0.4rem 0.6rem' }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-1">
                <Col md={12}>
                  <Form.Group className="mb-0">
                    <Form.Label className="fw-bold" style={{ fontSize: '0.8rem', marginBottom: '0.2rem' }}>Nombre de la Receta *</Form.Label>
                    <Form.Control
                      type="text"
                      className="dh-form-control"
                      placeholder="Ej: Arroz con Pollo"
                      value={formData.nombre || ''}
                      onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                      required
                      maxLength={255}
                      style={{ fontSize: '0.8rem', padding: '0.4rem 0.6rem' }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-1">
                <Col md={12}>
                  <Form.Group className="mb-0">
                    <Form.Label className="fw-bold" style={{ fontSize: '0.8rem', marginBottom: '0.2rem' }}>Descripción</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      className="dh-form-control"
                      placeholder="Descripción de la receta..."
                      value={formData.descripcion || ''}
                      onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                      style={{ fontSize: '0.8rem', padding: '0.4rem 0.6rem' }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Detalles de la Receta */}
              <div className="mb-1">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <h6 className="mb-0 fw-bold" style={{ fontSize: '0.85rem' }}>Detalles de la Receta (Insumos)</h6>
                  <Button
                    type="button"
                    className="btn-dh-primary"
                    onClick={handleAddDetalle}
                    style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
                  >
                    <FaPlus className="me-1" />
                    Agregar Insumo
                  </Button>
                </div>

                {formData.detalles.length === 0 ? (
                  <Alert variant="info" style={{ fontSize: '0.75rem', padding: '0.5rem', marginBottom: '0' }}>
                    No hay insumos agregados. Haga clic en "Agregar Insumo" para agregar ingredientes a la receta.
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table striped bordered hover size="sm" style={{ fontSize: '0.75rem', marginBottom: '0' }}>
                      <thead>
                        <tr>
                          <th style={{ fontSize: '0.75rem', padding: '0.4rem' }}>Insumo</th>
                          <th style={{ fontSize: '0.75rem', padding: '0.4rem' }}>Cantidad</th>
                          <th style={{ fontSize: '0.75rem', padding: '0.4rem' }}>Unidad</th>
                          <th style={{ width: '70px', fontSize: '0.75rem', padding: '0.4rem' }}>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.detalles.map((detalle, index) => (
                          <tr key={index}>
                            <td style={{ padding: '0.4rem' }}>
                              <Form.Select
                                className="dh-form-control"
                                value={detalle.insumo_id || ''}
                                onChange={(e) => handleUpdateDetalle(index, 'insumo_id', e.target.value)}
                                required
                                style={{ fontSize: '0.75rem', padding: '0.3rem 0.5rem' }}
                              >
                                <option value="">Seleccione insumo</option>
                                {insumos.map((insumo) => (
                                  <option key={insumo.id} value={insumo.id}>
                                    {insumo.nombre}
                                  </option>
                                ))}
                              </Form.Select>
                            </td>
                            <td style={{ padding: '0.4rem' }}>
                              <Form.Control
                                type="number"
                                step="0.0001"
                                min="0"
                                className="dh-form-control"
                                placeholder="0.0000"
                                value={detalle.cantidad || ''}
                                onChange={(e) => handleUpdateDetalle(index, 'cantidad', e.target.value)}
                                required
                                style={{ fontSize: '0.75rem', padding: '0.3rem 0.5rem' }}
                              />
                            </td>
                            <td style={{ padding: '0.4rem' }}>
                              <Form.Select
                                className="dh-form-control"
                                value={detalle.unidad_uso_id || ''}
                                onChange={(e) => handleUpdateDetalle(index, 'unidad_uso_id', e.target.value)}
                                required
                                style={{ fontSize: '0.75rem', padding: '0.3rem 0.5rem' }}
                              >
                                <option value="">Seleccione unidad</option>
                                {unidades.map((unidad) => (
                                  <option key={unidad.id} value={unidad.id}>
                                    {unidad.codigo} - {unidad.nombre}
                                  </option>
                                ))}
                              </Form.Select>
                            </td>
                            <td style={{ padding: '0.4rem' }}>
                              <Button
                                type="button"
                                variant="danger"
                                size="sm"
                                onClick={() => handleRemoveDetalle(index)}
                                style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}
                              >
                                <FaTrash />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>
            </Form>
          </Modal.Body>
          <Modal.Footer style={{ padding: '0.5rem 0.75rem' }}>
            <Button 
              className="btn-dh-secondary" 
              onClick={handleCloseModal}
              disabled={loading}
              style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
            >
              <FaTimes className="me-1" />
              Cancelar
            </Button>
            <Button
              className="btn-dh-info"
              onClick={() => handleSaveReceta(false)}
              disabled={loading || !formData.codigo_interno || !formData.nombre || formData.nombre.trim().length < 3}
              style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', border: 'none', color: 'white' }}
            >
              <FaSave className="me-1" />
              Guardar Detalle
            </Button>
            <Button
              className="btn-dh-primary"
              onClick={() => handleSaveReceta(true)}
              disabled={loading || !formData.codigo_interno || !formData.nombre || formData.nombre.trim().length < 3}
              style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
            >
              <FaCheck className="me-1" />
              Guardar y Cerrar
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Modal de confirmación de eliminación */}
      <ModalConfirmacion
        show={showConfirmModal}
        onHide={() => {
          setShowConfirmModal(false);
          setRecetaToDelete(null);
        }}
        onConfirm={confirmDeleteReceta}
        title="Confirmar Eliminación"
        message={
          recetaToDelete
            ? `¿Está seguro de eliminar la receta "${recetaToDelete.nombre}"?${recetaToDelete.total_detalles > 0 ? ` Se eliminarán también ${recetaToDelete.total_detalles} detalle${recetaToDelete.total_detalles > 1 ? 's' : ''} asociado${recetaToDelete.total_detalles > 1 ? 's' : ''}.` : ''}`
            : ""
        }
        confirmText="Eliminar"
        cancelText="Cancelar"
        loading={loading}
        variant="danger"
      />

      {/* Modal de Indicaciones */}
      <Modal 
        show={showIndicacionesModal} 
        onHide={() => setShowIndicacionesModal(false)} 
        size="lg" 
        centered
      >
        <Modal.Header style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
          color: 'white',
          border: 'none'
        }}>
          <Modal.Title style={{ fontSize: '1.1rem' }}>
            <FaInfoCircle className="me-2" />
            Indicaciones para Carga Masiva de Recetas
          </Modal.Title>
          <Button
            variant="link"
            onClick={() => setShowIndicacionesModal(false)}
            style={{ color: 'white', textDecoration: 'none', fontSize: '1.5rem' }}
          >
            <FaTimes />
          </Button>
        </Modal.Header>
        <Modal.Body style={{ padding: '1.5rem', fontSize: '0.9rem' }}>
          <Alert variant="info" className="mb-3">
            <strong>Formato del Archivo Excel:</strong> El archivo debe contener recetas con su cabecera y detalles. 
            Cada receta se compone de una fila de cabecera (tipo "C") seguida de una o más filas de detalle (tipo "D").
          </Alert>

          <h6 className="fw-bold mb-3" style={{ fontSize: '1rem', color: '#1e40af' }}>
            Estructura de Columnas
          </h6>
          
          <div className="mb-4">
            <h6 className="fw-bold mb-2" style={{ fontSize: '0.95rem' }}>
              Columnas Obligatorias (en todas las filas):
            </h6>
            <ul style={{ fontSize: '0.875rem', paddingLeft: '1.5rem' }}>
              <li className="mb-2">
                <strong>codigo_interno</strong> (Columna 1): Código único de la receta. Debe ser el mismo para la cabecera y todos sus detalles.
              </li>
              <li className="mb-2">
                <strong>tipo_fila</strong> (Columna 2): Debe ser <Badge bg="primary">C</Badge> para Cabecera o <Badge bg="success">D</Badge> para Detalle.
              </li>
            </ul>
          </div>

          <div className="mb-4">
            <h6 className="fw-bold mb-2" style={{ fontSize: '0.95rem' }}>
              Para Filas de Cabecera (tipo_fila = "C"):
            </h6>
            <ul style={{ fontSize: '0.875rem', paddingLeft: '1.5rem' }}>
              <li className="mb-1"><strong>nombre</strong>: Nombre de la receta (obligatorio, mínimo 3 caracteres)</li>
              <li className="mb-1"><strong>descripcion</strong>: Descripción de la receta (opcional)</li>
              <li className="mb-1"><strong>calorias_por_porcion</strong>: Calorías por porción (opcional, por defecto 0.00)</li>
              <li className="mb-1"><strong>codigo_interno_insumo</strong>, <strong>cantidad</strong>, <strong>unidad_uso_id</strong>: Deben estar vacíos</li>
            </ul>
          </div>

          <div className="mb-4">
            <h6 className="fw-bold mb-2" style={{ fontSize: '0.95rem' }}>
              Para Filas de Detalle (tipo_fila = "D"):
            </h6>
            <ul style={{ fontSize: '0.875rem', paddingLeft: '1.5rem' }}>
              <li className="mb-1"><strong>codigo_interno_insumo</strong>: Código interno del insumo (obligatorio, debe existir en el sistema)</li>
              <li className="mb-1"><strong>cantidad</strong>: Cantidad del insumo para 1 porción (obligatorio, debe ser ≥ 0)</li>
              <li className="mb-1"><strong>unidad_uso_id</strong>: ID de la unidad de medida (obligatorio, debe existir en el sistema)</li>
              <li className="mb-1"><strong>nombre</strong>, <strong>descripcion</strong>, <strong>calorias_por_porcion</strong>: Deben estar vacíos</li>
            </ul>
          </div>

          <div className="mb-4">
            <h6 className="fw-bold mb-2" style={{ fontSize: '0.95rem' }}>
              Ejemplo de Estructura:
            </h6>
            <div style={{ overflowX: 'auto' }}>
              <Table striped bordered size="sm" style={{ fontSize: '0.8rem' }}>
                <thead style={{ background: '#374151', color: 'white' }}>
                  <tr>
                    <th>codigo_interno</th>
                    <th>tipo_fila</th>
                    <th>nombre</th>
                    <th>descripcion</th>
                    <th>calorias_por_porcion</th>
                    <th>codigo_interno_insumo</th>
                    <th>cantidad</th>
                    <th>unidad_uso_id</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ background: '#e0f2fe' }}>
                    <td><strong>REC-001</strong></td>
                    <td><Badge bg="primary">C</Badge></td>
                    <td>Arroz con Pollo</td>
                    <td>Plato tradicional</td>
                    <td>350.50</td>
                    <td></td>
                    <td></td>
                    <td></td>
                  </tr>
                  <tr>
                    <td><strong>REC-001</strong></td>
                    <td><Badge bg="success">D</Badge></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td>INS-001</td>
                    <td>0.200</td>
                    <td>2</td>
                  </tr>
                  <tr>
                    <td><strong>REC-001</strong></td>
                    <td><Badge bg="success">D</Badge></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td>INS-002</td>
                    <td>0.150</td>
                    <td>2</td>
                  </tr>
                  <tr style={{ background: '#e0f2fe' }}>
                    <td><strong>REC-002</strong></td>
                    <td><Badge bg="primary">C</Badge></td>
                    <td>Lentejas</td>
                    <td>Guiso de lentejas</td>
                    <td>280.00</td>
                    <td></td>
                    <td></td>
                    <td></td>
                  </tr>
                  <tr>
                    <td><strong>REC-002</strong></td>
                    <td><Badge bg="success">D</Badge></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td>INS-003</td>
                    <td>0.300</td>
                    <td>2</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>

          <Alert variant="warning" className="mb-0">
            <strong>Importante:</strong>
            <ul className="mb-0 mt-2" style={{ fontSize: '0.85rem', paddingLeft: '1.5rem' }}>
              <li>Cada receta debe tener exactamente <strong>1 fila de cabecera (C)</strong></li>
              <li>Puede tener <strong>0 o más filas de detalle (D)</strong></li>
              <li>El <strong>codigo_interno</strong> debe ser único en todo el archivo</li>
              <li>Los <strong>codigo_interno_insumo</strong> y <strong>unidad_uso_id</strong> deben existir previamente en el sistema</li>
              <li>La columna <strong>tipo_fila</strong> debe ser la <strong>segunda columna</strong> del archivo</li>
            </ul>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            className="btn-dh-primary" 
            onClick={() => setShowIndicacionesModal(false)}
          >
            Entendido
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MantenimientoRecetas;

